import { any, anyPass, is, isNil, map, mapObjIndexed, not, pipe, propEq, startsWith } from 'ramda';
import { generateRandomGuid4 } from '../../../monitoring/utils';
import { parentConnection, ParentConnectionEvent } from '../../../services/parentConnection';
import { mapIndexed } from '../../../utils/array';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';
import { TMMGRSettings } from '../../../utils/hoc/withMMGRSettings/withMMGRSettings.types';
import { useEffectButSkipMount } from '../../../utils/hooks/useEffectButSkipMount';
import { useUploadingFiles } from '../../../utils/hooks/useFilesUploader';
import { UploadStatus } from '../../../utils/hooks/useFilesUploader/types';
import { getRectFitScale, IRect, rotate } from '../../../utils/math';
import { MediaManagerSDKUploadingFile } from '../../../utils/media-manager-sdk';
import { memoize } from '../../../utils/memoize';
import {
  createObjectUrl,
  getItemFromServerResponse,
  getOrientation,
  getSecureMediaType,
  getUploadingFileProps,
} from '../../../utils/uploadingFile';

const getItemIdForEmittingToParent = memoize(generateRandomGuid4);

const THUMB_SIZE = 100;

const imagesMap = new Map();

const canvas = document.createElement('canvas');

const getThumbnailSmallBase64 = (file) => {
  if (!file.type) {
    return null;
  }

  if (!startsWith('image', file.type)) {
    return null;
  }

  const valueInCache = imagesMap.get(file);

  if (Array.isArray(valueInCache)) {
    return valueInCache;
  }

  if (is(Image, valueInCache)) {
    return null;
  }

  const src = createObjectUrl(file);

  const image = new Image();
  imagesMap.set(file, image);

  const imageLoadPromise = new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  const orientationPromise = getOrientation(file);

  image.src = src;

  Promise.all([orientationPromise, imageLoadPromise]).then(([orientation]) => {
    const ctx = canvas.getContext('2d');

    const scale = getRectFitScale({ width: THUMB_SIZE, height: THUMB_SIZE }, image);

    const newImageWidth = Math.round(image.width * scale);
    const newImageHeight = Math.round(image.height * scale);

    const rad = (orientation.rotate / 180) * Math.PI;

    if (rad === 0) {
      // no modifications needed

      canvas.width = newImageWidth;
      canvas.height = newImageHeight;

      ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, newImageWidth, newImageHeight);
    } else {
      const rotatedRect = pipe<number, IRect, IRect, IRect, IRect>(
        rotate,
        mapObjIndexed(Math.abs),
        mapObjIndexed(Math.round),
      )(rad, image);

      canvas.width = Math.round(rotatedRect.width * scale);
      canvas.height = Math.round(rotatedRect.height * scale);

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(
        image,
        0,
        0,
        image.width,
        image.height,
        -newImageWidth / 2,
        -newImageHeight / 2,
        newImageWidth,
        newImageHeight,
      );
    }

    imagesMap.set(file, [
      null,
      ctx.getImageData(0, 0, newImageWidth, newImageHeight).data.buffer,
      newImageWidth,
      newImageHeight,
    ]);
  });

  return null;
};

const isTransferred = propEq('byteLength', 0);

const transformUploadingFile = memoize((uploadingFile, isSecureContext, privateMediaRestAPI, namespace) => {
  const { status, bytesLoaded } = uploadingFile;
  const { mediaType } = getUploadingFileProps({ uploadingFile, isSecureContext, privateMediaRestAPI });

  const newStatus = uploadingFile.isAborted() ? UploadStatus.Aborted : status;

  const originalFile = uploadingFile.source.file;
  const uploadedFile = newStatus === UploadStatus.Succeeded ? getItemFromServerResponse(uploadingFile) : null;

  return {
    status: newStatus,
    assetId: uploadedFile && uploadedFile.id,
    itemId: getItemIdForEmittingToParent(originalFile),
    mediaType: getSecureMediaType(mediaType, namespace) || mediaType,
    namespace,
    bytesLoaded,
    bytesTotal: uploadingFile.getFileSize(),
    name: originalFile.name,
  };
});

const emitUploadProgressToParent = (queue: MediaManagerSDKUploadingFile[], mmgrSettings: TMMGRSettings) => {
  const { isSecureContext, privateMediaRestAPI, namespace } = mmgrSettings;

  const thumbnailInfo = map((uploadingFile) => {
    const data = getThumbnailSmallBase64(uploadingFile.source.file);
    return Array.isArray(data) ? data : [null, null, null, null];
  }, queue);

  const thumbnailBuffers = mapIndexed((_, index) => thumbnailInfo[index][1], queue);

  // @ts-expect-error
  const areAllReadyToTransfer = not(anyPass([any(isNil), any(isTransferred)])(thumbnailBuffers));

  parentConnection.emit(
    ParentConnectionEvent.UploadProgress,
    mapIndexed((uploadingFile: MediaManagerSDKUploadingFile, index) => {
      const [, thumbnailBuffer, thumbnailWidth, thumbnailHeight] = thumbnailInfo[index];

      return {
        ...transformUploadingFile(uploadingFile, isSecureContext, privateMediaRestAPI, namespace),
        thumbnailWidth,
        thumbnailHeight,
        thumbnailBuffer: areAllReadyToTransfer ? thumbnailBuffer : null,
      };
    }, queue),
    areAllReadyToTransfer ? thumbnailBuffers : null,
  );
};

export const useEmitUploadProgressToParent = () => {
  const { mmgrSettings } = useMMGRSettings();
  const { uploadingFiles } = useUploadingFiles();

  useEffectButSkipMount(() => {
    emitUploadProgressToParent(uploadingFiles, mmgrSettings);
  }, [uploadingFiles]);
};
